import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/motion/Reveal";
import { RainbowArcs } from "../components/motion/RainbowArcs";
import { QuestionList } from "../components/shared/QuestionList";
import {
  ERA_MARKS,
  MOSHIACH_FOR_GENTILES,
  MOSHIACH_PRACTICES,
  MOSHIACH_FAQS,
} from "../data/moshiach";

const SECTIONS = [
  { id: "era", label: "What the world becomes" },
  { id: "gentile", label: "For a gentile in particular" },
  { id: "now", label: "How to live now" },
  { id: "questions", label: "Questions" },
];

const SectionHead = ({ id, eyebrow, title, body }) => (
  <Reveal className="mb-12">
    <p id={id} className="eyebrow scroll-mt-32">
      {eyebrow}
    </p>
    <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">{title}</h2>
    {body && <p className="mt-5 max-w-xl text-base leading-relaxed text-ink-soft">{body}</p>}
  </Reveal>
);

export default function Moshiach() {
  return (
    <div data-testid="moshiach-page">
      <PageHeader
        testId="moshiach-header"
        eyebrow="Moshiach · The Messianic Era"
        lines={["A world", "set right."]}
        lede={
          <>
            <span className="block font-serif text-2xl italic leading-snug text-ink sm:text-3xl">
              “The earth shall be full of the knowledge of the Lord, as the waters cover the sea.”
            </span>
            <span className="mt-5 block">
              The Torah's hope is not an escape from the world but its repair. The era of Moshiach
              is what the world is supposed to become — and the prophets placed every nation at
              the centre of that picture.
            </span>
          </>
        }
      />

      <nav aria-label="On this page" className="mx-auto mb-16 flex max-w-7xl flex-wrap gap-x-7 gap-y-3 px-5 sm:px-8">
        {SECTIONS.map((s, i) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            data-testid={`moshiach-nav-${s.id}`}
            className="link-underline flex items-baseline gap-2 text-sm text-ink-soft hover:text-ink"
          >
            <span className="font-mono text-[10px] text-terracotta">{String(i + 1).padStart(2, "0")}</span>
            {s.label}
          </a>
        ))}
      </nav>

      {/* 01 — The era */}
      <section data-testid="moshiach-era" className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead id="era" eyebrow="01 · The era of Moshiach" title="What the world is supposed to become." />
          </div>
          <Reveal delay={0.1} className="prose-warm max-w-2xl text-base leading-[1.85] text-ink-soft sm:text-lg lg:col-span-7 lg:col-start-6">
            <p className="drop-cap">
              Every tradition has a picture of how the story ends. The Torah's picture is unusual in
              one respect: it is set here, on this earth, among these nations. The prophets do not
              describe souls leaving the world; they describe the world healed. Swords beaten into
              ploughshares. Famine forgotten. The poor judged with righteousness. And one thing
              underneath all of it — 'the earth full of the knowledge of the Lord'.
            </p>
            <p>
              The Rambam is careful to say that nothing in nature will change (Laws of Kings 12:1).
              Rivers will not run with wine; the sun will rise as it always has. What changes is
              humanity. When every person knows that there is a Creator and lives accordingly, the
              causes of war, want and cruelty simply fall away. 'The occupation of the entire world
              will be only to know G-d' (12:5) — that is the whole description of the age.
            </p>
            <p>
              This is why the era is called by the name of the one who ushers it in — Moshiach, 'the
              anointed', a leader from the line of David who brings the Jewish people home and the
              nations to the knowledge of G-d. But the point was never the king. The point is the
              world he leads into being — a world that the Seven Laws already describe in miniature.
            </p>
          </Reveal>
        </div>

        <Stagger className="mt-20 grid grid-cols-1 gap-px bg-ink/10 sm:grid-cols-2 lg:grid-cols-3" stagger={0.06}>
          {ERA_MARKS.map((m, i) => (
            <StaggerItem key={m.title}>
              <div data-testid={`moshiach-era-mark-${i + 1}`} className="flex h-full flex-col bg-parchment p-7 lg:p-8">
                <span className="font-mono text-[10px] tracking-[0.22em] text-terracotta">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-serif text-2xl leading-snug text-ink">{m.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-soft">{m.body}</p>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">{m.ref}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* 03 — For a gentile */}
      <section data-testid="moshiach-gentile" className="relative mt-32 overflow-hidden bg-ink py-24 text-parchment lg:py-32">
        <RainbowArcs
          animate={false}
          className="pointer-events-none absolute -top-10 right-[-10%] w-[70vw] max-w-[900px] rotate-180 text-terracotta/25"
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-16">
            <p id="gentile" className="eyebrow scroll-mt-32">
              02 · For a gentile in particular
            </p>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
              Not a spectator. <span className="italic text-parchment/60">A builder.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-parchment/70">
              Six things the sources say about the place of the nations — and of each person among them
              — in the world Moshiach brings.
            </p>
          </Reveal>
          <Stagger className="grid grid-cols-1 gap-px bg-parchment/10 md:grid-cols-2" stagger={0.07}>
            {MOSHIACH_FOR_GENTILES.map((item, i) => (
              <StaggerItem key={item.title}>
                <div data-testid={`moshiach-gentile-point-${i + 1}`} className="flex h-full flex-col bg-ink p-8 lg:p-10">
                  <span className="font-serif text-5xl leading-none text-terracotta/80">0{i + 1}</span>
                  <h3 className="mt-6 font-serif text-2xl leading-snug sm:text-3xl">{item.title}</h3>
                  <p className="mt-4 flex-1 text-sm leading-[1.8] text-parchment/75 sm:text-base">{item.body}</p>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-parchment/40">{item.ref}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 04 — Now */}
      <section data-testid="moshiach-now" className="mx-auto mt-32 max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead
              id="now"
              eyebrow="03 · How to live now"
              title="In expectation, without anxiety."
              body="The tradition asks for a posture: steady work and daily hope. Six practices for a Noahide who takes the Messianic promise seriously."
            />
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <ol className="border-t hairline">
              {MOSHIACH_PRACTICES.map((p, i) => (
                <Reveal key={p.title} y={16}>
                  <li data-testid={`moshiach-practice-${i + 1}`} className="grid grid-cols-[3rem_1fr] gap-5 border-b hairline py-6 sm:grid-cols-[4rem_1fr] sm:gap-8">
                    <span className="font-serif text-3xl leading-none text-terracotta">{i + 1}</span>
                    <div>
                      <h3 className="font-serif text-2xl leading-snug text-ink">{p.title}</h3>
                      <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-soft sm:text-base">{p.body}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* 05 — Questions */}
      <section data-testid="moshiach-questions" className="mx-auto mt-32 max-w-7xl px-5 pb-28 sm:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead id="questions" eyebrow="04 · Questions" title="What people ask." />
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal>
              <QuestionList items={MOSHIACH_FAQS} idPrefix="moshiach-faq" />
            </Reveal>
            <Reveal className="mt-20 flex flex-col gap-6 border hairline bg-white p-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="eyebrow">Go deeper</p>
                <p className="mt-2 font-serif text-2xl leading-snug text-ink">
                  Essay: The World to Come and the Nations
                </p>
              </div>
              <Link
                to="/study/essays/the-world-to-come"
                data-testid="moshiach-essay-link"
                className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-ink px-6 py-3.5 text-sm font-medium text-parchment transition-colors duration-300 hover:bg-terracotta"
              >
                Read the essay
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
